"use client";

import { motion } from "motion/react";
import { useMessages, useTranslations } from "next-intl";
import {
	Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
	Stack,
	Typography,
	useTheme,
} from "@/components/controls/base-controls";
import { CheckBoxIcon } from "@/components/controls/icons";

import {
	HEADER_HEIGHT_FULL,
	WEBPAGE_CORE_CONTAINER,
} from "@/core/constants/webpages";

const Standards = () => {

    const theme = useTheme();
	const t = useTranslations();
	const messages = useMessages();
	const items = ((messages.standards as Record<string, string[]>).items) ?? [];

    return (
        <Stack
            sx={{
                ...theme.mixins.bgImage({
                    xs: { src: "https://general-images.navairra.com/mountain_mist_420x900.png", height: "100svh" },
                    sm: { src: "https://general-images.navairra.com/mountain_mist_900x420.png", height: "100svh" },
                    md: { src: "https://general-images.navairra.com/mountain_mist_1200x428.png", height: `calc(100svh - ${HEADER_HEIGHT_FULL})` },
                    lg: { src: "https://general-images.navairra.com/mountain_mist_1920x680.png", height: `calc(100svh - ${HEADER_HEIGHT_FULL})` },
                    xl: { src: "https://general-images.navairra.com/mountain_mist_2538x900.png", height: `calc(100svh - ${HEADER_HEIGHT_FULL})` },
                    backgroundPosition: "center bottom",
                }),
                display: "flex",
                alignItems: "flex-end"
            }}
        >
            <Box
                sx={{
                    margin: "0 auto",
                    width: WEBPAGE_CORE_CONTAINER,
                    maxWidth: "100%",
                    padding: { xs: "2em 0 0", md: "8em 1em 4em" },
                    display: "grid",
                    gridTemplateRows: { xs: "1fr 2fr", md: "none" },
                    gridTemplateColumns: { xs: "none", md: "1fr 1fr" },
                    gap: { xs: "1em", md: "1em" },
                    height: {
                        xs: "100svh",
                        md: `calc(100svh - ${HEADER_HEIGHT_FULL})`,
                    }
                }}
            >
                <Stack
                    component={motion.div} 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    direction="column"
                    sx={{
                        padding: { xs: "0 1em", md: "1em 1em 1em 6em" },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: { xs: "flex-end", md: "flex-start" }
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            color: "var(--mui-palette-lightest)",
                        }}
                    >
                        {t("standards.title")}
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            margin: { xs: "2em 0 1em", md: "3em 0 1em" },
                            color: "var(--mui-palette-lightest)",
                            maxWidth: { xs: "100%", md: "60ch" }
                        }}
                    >
                        {t("standards.description")}
                    </Typography>
                    
                </Stack>
                <List 
                    component={motion.div} 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    sx={{
                        padding: { xs: "0 1em 1em", md: "1em 3em" },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        gap: 2
                }}>
                    {items.map((text) => (
                        <ListItem key={text} disableGutters>
                            <ListItemIcon sx={{ minWidth: { xs: "1.9em", md: "2.5em" } }}>
                                <CheckBoxIcon sx={{  color: "var(--mui-palette-light)", opacity: "50%", transform: {xs: "scale(1)", md: "scale(1.5)" } }} />
                            </ListItemIcon>
                            <ListItemText 
                                primary={text} 
                                slotProps={{ primary: { sx: { fontSize: {xs: ".875rem", md: "1rem" } } }}}
                                sx={{ color:"var(--mui-palette-lightest)", margin: 0 }} // Removes default margins for perfect centering
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Stack>
    );

}

export default Standards;
